// @flow

import React, { type Node } from 'react';
import { useIncomingPayment, getPaymentType, getCountry, getPaymentTypeCode } from '@tectonics/prs/incoming-payment';
import { Header as SafePaymentHeader } from '../../organisms/header';
import { DesktopVoucherHeader } from './commons/voucher';
import { CreditCardHeader } from './creditcard-page/header';
import { Empty } from '../../atoms';

export function Header(): Node {
  const incomingPayment = useIncomingPayment();
  const paymentType = getPaymentType(incomingPayment) || '';
  const paymentTypeCode = getPaymentTypeCode(incomingPayment) || '';
  const country = getCountry(incomingPayment) || '';

  switch (country) {
    case 'br':
      return getBrazilianHeader(paymentType);
    case 'ar':
      return getArgentineHeader(paymentType);
    case 'bo':
      return getBolivianHeader(paymentType);
    case 'cl':
      return getChileanHeader(paymentType);
    case 'co':
      return getColombianHeader(paymentType);
    case 'ec':
      return getEcuadorianHeader(paymentType);
    case 'mx':
      return getMexicanHeader(paymentType);
    case 'pe':
      return getPeruvianHeader(paymentType);
    case 'uy':
      return getUruguayanHeader(paymentType);
    case 'cr':
      return getCostaRicanHeader(paymentType);
    case 'py':
      return getParaguayanHeader(paymentType);
    case 'pa':
      return getPanamanianHeader(paymentType);
    case 'sv':
      return getElSalvadorHeader(paymentType);
    case 'gt':
      return getGuatemalanHeader(paymentType);
    case 'do':
      return getDominicanHeader(paymentType);
    case 'za':
      return getSouthAfricanHeader(paymentType, paymentTypeCode);
    case 'ke':
      return getKenyanHeader(paymentType);
    case 'ng':
      return getNigerianHeader(paymentType);
    case 'in':
      return getIndiaHeader(paymentType);
    case 'tz':
      return getTanzanianHeader(paymentType);
    case 'ug':
      return getUgandaHeader(paymentType);
    case 'zm':
      return getZambiaHeader(paymentType);
    case 'eg':
      return getEgyptHeader(paymentType);
    case 'gh':
      return getGhanaHeader(paymentType);
    case 'jm':
      return getJamaicaHeader(paymentType);
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for country "${country}"`);
  }
}

function getBrazilianHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    case 'boleto':
      return <DesktopVoucherHeader />;
    case 'banktransfer':
    case 'eft':
    case 'tef':
    case 'externalpayprovider':
    case 'ewallet':
      return <Empty />;
    case 'pix':
      return <SafePaymentHeader />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Brazil`);
  }
}

function getMexicanHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    case 'oxxo':
    case 'cash':
    case 'spei':
    case 'externalpayprovider':
      return <SafePaymentHeader />;
    case 'ewallet':
      return <Empty />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Mexico`);
  }
}

function getArgentineHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'cash':
      return <SafePaymentHeader />;
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    case 'ewallet':
      return <Empty />;
    case 'banktransfer':
      return <SafePaymentHeader />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Argentina`);
  }
}

function getColombianHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
      return <CreditCardHeader />;
    case 'cash':
    case 'ewallet':
    case 'externalpayprovider':
      return <SafePaymentHeader />;
    case 'eft':
      return <Empty />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Colombia`);
  }
}

function getPeruvianHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
      return <CreditCardHeader />;
    case 'pagoefectivo':
      return <SafePaymentHeader />;
    case 'safetypay':
    case 'safetypay-cash':
    case 'ewallet':
    case 'banktransfer':
      return <Empty />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Peru`);
  }
}

function getChileanHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
      return <CreditCardHeader />;
    case 'ewallet':
    case 'multicaja':
    case 'webpay':
    case 'sencillito':
    case 'externalpayprovider':
    case 'servipag':
    case 'safetypay':
    case 'safetypay-cash':
    case 'safetypay-online':
    case 'banktransfer':
      return <Empty />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Chile`);
  }
}

function getBolivianHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'cash':
      return <SafePaymentHeader />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Bolivia`);
  }
}

function getEcuadorianHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'safetypay-cash':
    case 'safetypay-online':
      return <Empty />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType || ''}"`);
  }
}

function getUruguayanHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Uruguay`);
  }
}

function getCostaRicanHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    case 'cash':
      return <SafePaymentHeader />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Costa Rica`);
  }
}

function getParaguayanHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Paraguay`);
  }
}

function getPanamanianHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    case 'cash':
      return <SafePaymentHeader />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Panama`);
  }
}

function getElSalvadorHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    case 'cash':
      return <SafePaymentHeader />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: El Salvador`);
  }
}

function getGuatemalanHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    case 'cash':
      return <SafePaymentHeader />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Guatemala`);
  }
}

function getDominicanHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Dominican Republic`);
  }
}

function getSouthAfricanHeader(paymentType: string, paymentTypeCode: string): Node {
  switch (paymentType) {
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    case 'externalpayprovider':

      return <Empty />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: South Africa`);
  }
}

function getKenyanHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    case 'ewallet':
      return <Empty />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Kenya`);
  }
}

function getNigerianHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    case 'externalpayprovider':
    case 'ewallet':
    case 'banktransfer':
      return <Empty />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Nigeria`);
  }
}

function getIndiaHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'ewallet':
      return <Empty />;
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: India`);
  }
}

function getTanzanianHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'ewallet':
      return <Empty />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Tanzania`);
  }
}

function getUgandaHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'ewallet':
      return <Empty />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Uganda`);
  }
}

function getZambiaHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'ewallet':
      return <Empty />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Zambia`);
  }
}

function getEgyptHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    case 'ewallet':
      return <Empty />;
    case 'externalpayprovider':
      return <SafePaymentHeader />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Egypt`);
  }
}

function getJamaicaHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'creditcard':
    case 'debitcard':
      return <CreditCardHeader />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Jamaica`);
  }
}

function getGhanaHeader(paymentType: string): Node {
  switch (paymentType) {
    case 'ewallet':
      return <Empty />;
    default:
      throw new Error(`PendingPage.Header: There is no implemented header for payment type "${paymentType}" and country: Ghana`);
  }
}
